/**
 * EvalCarDiagram — 평가 상세 차량 4면도 시각화 (#241, 앱 신방식 웹 도입).
 *
 * Flutter 앱 (erp/app lib/features/eval/) 의 Car3ViewDiagram + EvalDamageDiagram /
 * EvalPaintDiagram 포팅 — 차량 도면 PNG 위에 부위 폴리곤(evalCarGeometry.js)을
 * 반투명 채색. 사고부위는 4면(외판+하부 골격) 2×2, 도막은 외판 3면(좌/상/우) 한 줄.
 *
 * 데이터 (eval_results):
 *  - damage_data {partKey: {past, current}} 신규 체계 + 레거시 {status|type} 8종
 *    (X_WELD 등, 구 DamageCheckMap 작성분) → normalizeDamageEntry 로 흡수.
 *    레거시 부위키(fender_fl 등 구 top-view 키)는 LEGACY_KEY_ALIAS 로 zone 매핑.
 *  - paint_* 11 컬럼 (µm) → 임계값 ≤150 정상 / ≤250 도장 / 250+ 판금 (앱 토큰 색).
 *
 * 읽기 전용 — 입력용 DamageCheckMap(자가검차)·AuctionDetail 용도는 건드리지 않음.
 * 사용처: OrderDetail.js (사고 부위 / 도막 측정 섹션).
 */
import React, { useState } from 'react';
import { CAR4VIEW_BOX_W, CAR4VIEW_BOX_H, CAR4VIEW_PANELS } from './evalCarGeometry';
import carLeft from '../assets/car-4view/o-left.png';
import carTop from '../assets/car-4view/o-top.png';
import carBottom from '../assets/car-4view/o-bottom.png';
import carRight from '../assets/car-4view/o-right.png';

var PANEL_IMG = { left: carLeft, top: carTop, bottom: carBottom, right: carRight };
var PANEL_BY_KEY = {};
CAR4VIEW_PANELS.forEach(function(p) { PANEL_BY_KEY[p.key] = p; });

// ─── 사고부위 도메인 테이블 (앱 eval_damage.dart 패리티) ───────────────────

// 과거 이력 5종 (key, short, label, fg, bg)
export var PAST_TYPES = [
  { key: 'xx', short: 'XX', label: '교환', fg: '#1B5E20', bg: '#E8F5E9' },
  { key: 'pp', short: 'pp', label: '도장(판금)', fg: '#E65100', bg: '#FFF3E0' },
  { key: 'f', short: 'f', label: '꺾임', fg: '#AD1457', bg: '#FCE4EC' },
  { key: 'w', short: 'w', label: '판금/용접', fg: '#5D4037', bg: '#EFEBE9' },
  { key: 'm', short: 'm', label: '조정작업', fg: '#455A64', bg: '#ECEFF1' },
];

// 현재 상태 5종
export var CURRENT_TYPES = [
  { key: 'X', short: 'X', label: '교환필요', fg: '#E24B4A', bg: '#FCEBEB' },
  { key: 'P', short: 'P', label: '도장필요', fg: '#E65100', bg: '#FFF3E0' },
  { key: 'Q', short: 'Q', label: '판금필요', fg: '#0D47A1', bg: '#E3F2FD' },
  { key: 'A', short: 'A', label: '상처', fg: '#222222', bg: '#F5F5F5' },
  { key: 'C', short: 'C', label: '부식', fg: '#6A1B9A', bg: '#F3E5F5' },
];

export function typeByKey(key) {
  if (!key) return null;
  var all = PAST_TYPES.concat(CURRENT_TYPES);
  for (var i = 0; i < all.length; i++) if (all[i].key === key) return all[i];
  return null;
}

// 레거시 {status: 'X_WELD'} → 신규 체계 (앱 kLegacyMap 패리티)
var LEGACY_STATUS_MAP = {
  X_WELD: { isPast: true, key: 'xx' },
  X_BOLT: { isPast: true, key: 'xx' },
  W: { isPast: true, key: 'w' },
  P: { isPast: true, key: 'pp' },
  A: { isPast: false, key: 'A' },
  U: { isPast: false, key: 'A' },
  C: { isPast: false, key: 'C' },
  T: { isPast: false, key: 'A' },
};

// 신규 {past, current} + 레거시 {status|type} → {past, current} (앱 normalizeDamageEntry 패리티)
export function normalizeDamageEntry(raw) {
  if (!raw || typeof raw !== 'object') return { past: null, current: null };
  if ('past' in raw || 'current' in raw) {
    return {
      past: typeof raw.past === 'string' && raw.past ? raw.past : null,
      current: typeof raw.current === 'string' && raw.current ? raw.current : null,
    };
  }
  var mapped = LEGACY_STATUS_MAP[raw.status || raw.type];
  if (!mapped) return { past: null, current: null };
  return mapped.isPast ? { past: mapped.key, current: null } : { past: null, current: mapped.key };
}

// 구 DamageCheckMap 부위키 → zone partKey (외판만 상이 — _s 접미사. 하부/상면 키는 동일)
var LEGACY_KEY_ALIAS = {
  fender_fl: 'fender_fl_s', fender_fr: 'fender_fr_s',
  door_fl: 'door_fl_s', door_fr: 'door_fr_s',
  door_rl: 'door_rl_s', door_rr: 'door_rr_s',
  quarter_l: 'quarter_l_s', quarter_r: 'quarter_r_s',
};

// 부위 라벨 (앱 kPartLabels 패리티 — 레거시 top-view 키 포함)
export var PART_LABELS = {
  hood: '후드', fender_fl: '좌앞펜더', fender_fr: '우앞펜더',
  roof: '루프', door_fl: '좌앞문', door_fr: '우앞문',
  door_rl: '좌뒷문', door_rr: '우뒷문', trunk_top: '트렁크',
  quarter_l: '좌쿼터', quarter_r: '우쿼터',
  fender_fl_s: '좌앞펜더', a_pillar_l: 'A필러', door_fl_s: '좌앞문',
  b_pillar_l: 'B필러', door_rl_s: '좌뒷문', side_sill_l: '사이드실',
  c_pillar_l: 'C필러', quarter_l_s: '좌쿼터',
  fender_fr_s: '우앞펜더', a_pillar_r: 'A필러', door_fr_s: '우앞문',
  b_pillar_r: 'B필러', door_rr_s: '우뒷문', side_sill_r: '사이드실',
  c_pillar_r: 'C필러', quarter_r_s: '우쿼터',
  radiator_sup: '라디에이터', front_panel: '프론트패널',
  inside_panel_l: '좌인사이드', inside_panel_r: '우인사이드',
  sidemember_lf: '좌사이드멤버(앞)', sidemember_rf: '우사이드멤버(앞)',
  wheelhouse_lf: '좌앞휠하우스', wheelhouse_rf: '우앞휠하우스',
  cross_member: '크로스멤버', dash_panel: '대시패널',
  floor_front: '플로어패널', trunk_floor: '트렁크플로어',
  wheelhouse_lr: '좌뒷휠하우스', wheelhouse_rr: '우뒷휠하우스',
  sidemember_lr: '좌사이드멤버(뒤)', sidemember_rr: '우사이드멤버(뒤)',
  rear_panel: '리어패널',
};

// ─── 도막 도메인 (앱 kPaintParts / kPaintToZone / evalPaintColor 패리티) ────

export var PAINT_PARTS = [
  ['paint_hood', '본넷'], ['paint_roof', '루프'],
  ['paint_fender_l', '좌펜더'], ['paint_fender_r', '우펜더'],
  ['paint_door_fl', '좌앞문'], ['paint_door_fr', '우앞문'],
  ['paint_door_rl', '좌뒷문'], ['paint_door_rr', '우뒷문'],
  ['paint_trunk', '트렁크'], ['paint_quarter_l', '좌쿼터'], ['paint_quarter_r', '우쿼터'],
];

export var PAINT_TO_ZONE = {
  paint_hood: 'hood', paint_roof: 'roof', paint_trunk: 'trunk_top',
  paint_fender_l: 'fender_fl_s', paint_fender_r: 'fender_fr_s',
  paint_door_fl: 'door_fl_s', paint_door_fr: 'door_fr_s',
  paint_door_rl: 'door_rl_s', paint_door_rr: 'door_rr_s',
  paint_quarter_l: 'quarter_l_s', paint_quarter_r: 'quarter_r_s',
};

// 앱 AppColors.success/warning/danger — 정상 ≤150 / 도장 ≤250 / 판금 250+
var PAINT_GREEN = '#047857', PAINT_AMBER = '#B45309', PAINT_RED = '#B91C1C';

// #352 — null(미측정)과 0(정상, ≤150µm 그룹)을 구분한다. 0은 더 이상 회색이 아니다.
export function evalPaintColor(v) {
  if (v == null) return '#C8D1DD';
  if (v <= 150) return PAINT_GREEN;
  if (v <= 250) return PAINT_AMBER;
  return PAINT_RED;
}

function paintTint(v) {
  if (v == null) return '#F0F3F6';
  if (v <= 150) return '#EAF8F0';
  if (v <= 250) return '#FFF7E6';
  return '#FFEEEE';
}

// camel/snake 양쪽 수용 (구 CarPaintMap 정합 — API 는 snake, 일부 경로 camel)
function paintValue(data, snakeKey) {
  if (!data) return null;
  var camel = snakeKey.replace(/_([a-z])/g, function(m, c) { return c.toUpperCase(); });
  var v = data[snakeKey] != null ? data[snakeKey] : data[camel];
  var n = typeof v === 'string' ? parseInt(v, 10) : v;
  return typeof n === 'number' && !isNaN(n) ? n : null;
}

// ─── 패널 SVG 렌더 (앱 Car3ViewDiagram._Panel 패리티) ──────────────────────

// path bbox 중심 (사고 기호 배치용) — M/L 절대좌표 폴리곤 전제
var centerCache = {};
function pathCenter(d) {
  if (centerCache[d]) return centerCache[d];
  var nums = d.match(/-?\d+(\.\d+)?/g).map(Number);
  var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (var i = 0; i + 1 < nums.length; i += 2) {
    if (nums[i] < minX) minX = nums[i];
    if (nums[i] > maxX) maxX = nums[i];
    if (nums[i + 1] < minY) minY = nums[i + 1];
    if (nums[i + 1] > maxY) maxY = nums[i + 1];
  }
  var c = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  centerCache[d] = c;
  return c;
}

// props: { panelKey, resolveColor, resolveSymbol, caption? }
//   caption 미지정 시 지오메트리의 한국어 라벨(기존 동작). 다국어 화면은 번역 문자열을 넘긴다.
export function PanelSvg(props) {
  var panel = PANEL_BY_KEY[props.panelKey];
  var caption = props.caption === undefined ? panel.label : props.caption;
  return (
    <div style={{ flex: 1, minWidth: 0, background: '#F8FAFC', border: '1px solid #DDE3EA', borderRadius: 8, padding: 4, textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: '#667085', marginBottom: 2 }}>{caption}</div>
      <svg viewBox={'0 0 ' + CAR4VIEW_BOX_W + ' ' + CAR4VIEW_BOX_H} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* 채색 그룹(opacity 0.5) → 차체 선 PNG(multiply) → 사고 기호 순 (신고 384) */}
        <g opacity={0.5}>
          {panel.zones.map(function(z, i) {
            if (!z.partKey) return null;
            var color = props.resolveColor(z.partKey);
            if (!color) return null;
            return <path key={i} d={z.path} fill={color} fillRule="evenodd" stroke={color} strokeWidth={2} strokeLinejoin="round" />;
          })}
        </g>
        <image href={PANEL_IMG[panel.key]} x="0" y="0" width={CAR4VIEW_BOX_W} height={CAR4VIEW_BOX_H} style={{ mixBlendMode: 'multiply' }} />
        {panel.zones.map(function(z, i) {
          if (!z.partKey) return null;
          var color = props.resolveColor(z.partKey);
          var symbol = color && props.resolveSymbol ? props.resolveSymbol(z.partKey) : null;
          if (!symbol) return null;
          var c = pathCenter(z.path);
          return (
            // 채색 위 사고 기호 (pp/xx 등) — 흰 후광(stroke) + 채움색 (앱 #214 정합)
            <text key={i} x={c.x} y={c.y} textAnchor="middle" dominantBaseline="central"
              style={{ fontSize: 44, fontWeight: 700, fill: color, stroke: '#fff', strokeWidth: 6, paintOrder: 'stroke', pointerEvents: 'none' }}>
              {symbol}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// ─── 사고 부위 섹션 (다이어그램 4면 2×2 + 범례 토글 + 부위별 목록) ─────────

export function damageEntryFor(value, zoneKey) {
  // 직접 키 우선, 없으면 레거시 키(구 DamageCheckMap 작성분) 폴백
  var raw = value[zoneKey];
  if (raw == null) {
    for (var legacy in LEGACY_KEY_ALIAS) {
      if (LEGACY_KEY_ALIAS[legacy] === zoneKey) { raw = value[legacy]; break; }
    }
  }
  return normalizeDamageEntry(raw);
}

function typeChip(t) {
  return (
    <span key={t.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: t.fg, background: t.bg, padding: '3px 8px', borderRadius: 999 }}>
      <b>{t.short}</b> {t.label}
    </span>
  );
}

export function EvalDamageSection(props) {
  var value = props.value || {};
  var [legendOpen, setLegendOpen] = useState(false);

  var resolveColor = function(zoneKey) {
    var e = damageEntryFor(value, zoneKey);
    // 현재 상태 우선(매입 판단에 직접적), 없으면 과거 이력 (앱 정합)
    var t = typeByKey(e.current) || typeByKey(e.past);
    return t ? t.fg : null;
  };
  var resolveSymbol = function(zoneKey) {
    var e = damageEntryFor(value, zoneKey);
    var t = typeByKey(e.current) || typeByKey(e.past);
    return t ? t.short : null;
  };

  // 부위별 목록 — 과거/현재 그룹 (zone 미대응 키 포함, 데이터 손실 없음)
  var keys = Object.keys(value);
  var pastRows = [], currentRows = [];
  keys.forEach(function(k) {
    var e = normalizeDamageEntry(value[k]);
    var note = value[k] && value[k].note;
    if (e.past) pastRows.push({ key: k, type: typeByKey(e.past), note: note });
    if (e.current) currentRows.push({ key: k, type: typeByKey(e.current), note: note });
  });

  var renderGroup = function(title, rows) {
    if (!rows.length) return null;
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#667085', marginBottom: 4 }}>{title}</div>
        {rows.map(function(r) {
          return (
            <div key={r.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#222' }}>
                {PART_LABELS[r.key] || r.key}
                {r.note ? <span style={{ fontWeight: 400, color: '#98a2b3', marginLeft: 6 }}>{r.note}</span> : null}
              </span>
              {r.type && typeChip(r.type)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 480 }}>
      {/* 4면 2×2 — 상면/하부 · 좌측/우측 (앱 배치 정합) */}
      <div style={{ display: 'flex', gap: 6 }}>
        <PanelSvg panelKey="top" resolveColor={resolveColor} resolveSymbol={resolveSymbol} />
        <PanelSvg panelKey="bottom" resolveColor={resolveColor} resolveSymbol={resolveSymbol} />
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <PanelSvg panelKey="left" resolveColor={resolveColor} resolveSymbol={resolveSymbol} />
        <PanelSvg panelKey="right" resolveColor={resolveColor} resolveSymbol={resolveSymbol} />
      </div>

      <button onClick={function() { setLegendOpen(!legendOpen); }}
        style={{ marginTop: 10, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1px solid #DDE3EA', borderRadius: 8, padding: '7px 12px', fontSize: 11, fontWeight: 700, color: '#667085', cursor: 'pointer' }}>
        표기법 범례 <span>{legendOpen ? '▲' : '▼'}</span>
      </button>
      {legendOpen && (
        <div style={{ marginTop: 8, padding: 10, background: '#F8FAFC', border: '1px solid #E8EDF3', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#222', marginBottom: 6 }}>과거 이력</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{PAST_TYPES.map(typeChip)}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#222', margin: '10px 0 6px' }}>현재 상태</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{CURRENT_TYPES.map(typeChip)}</div>
        </div>
      )}

      {renderGroup('과거 이력', pastRows)}
      {renderGroup('현재 상태', currentRows)}
    </div>
  );
}

// ─── 도막 측정 섹션 (외판 3면 + 범례 + 부위별 µm 칩) ───────────────────────

var PAINT_GROUPS = [
  ['좌측', ['paint_fender_l', 'paint_door_fl', 'paint_door_rl', 'paint_quarter_l']],
  ['상면', ['paint_hood', 'paint_roof', 'paint_trunk']],
  ['우측', ['paint_fender_r', 'paint_door_fr', 'paint_door_rr', 'paint_quarter_r']],
];
var PAINT_LABELS = {};
PAINT_PARTS.forEach(function(p) { PAINT_LABELS[p[0]] = p[1]; });

function legendDot(color, text) {
  return (
    <span key={text} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#667085' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} /> {text}
    </span>
  );
}

export function EvalPaintSection(props) {
  var data = props.data;
  var byZone = {};
  PAINT_PARTS.forEach(function(p) { byZone[PAINT_TO_ZONE[p[0]]] = paintValue(data, p[0]); });

  var resolveColor = function(zoneKey) {
    var v = byZone[zoneKey];
    // #352 — 0(정상)도 측정값이므로 패널을 채운다. null(미측정)만 비운다.
    return v != null ? evalPaintColor(v) : null;
  };

  var paintChip = function(key) {
    var v = paintValue(data, key);
    var measured = v != null;
    var color = evalPaintColor(v);
    var display = v == null ? '-' : (v === 0 ? '정상 ≤150' : v);
    return (
      <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: paintTint(v), border: '1px solid ' + (measured ? color + '73' : '#DDE3EA'), borderRadius: 999, padding: '4px 10px' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#667085' }}>{PAINT_LABELS[key]}</span>
        <span style={{ fontSize: 12, fontWeight: 800, color: measured ? color : '#98a2b3' }}>{display}</span>
      </span>
    );
  };

  return (
    <div style={{ maxWidth: 480 }}>
      {/* 외판 3면 — 좌/상/우 (도막 = 외판 개념, 앱 정합) */}
      <div style={{ display: 'flex', gap: 6 }}>
        <PanelSvg panelKey="left" resolveColor={resolveColor} />
        <PanelSvg panelKey="top" resolveColor={resolveColor} />
        <PanelSvg panelKey="right" resolveColor={resolveColor} />
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        {legendDot(PAINT_GREEN, '정상 ≤150')}
        {legendDot(PAINT_AMBER, '도장 ≤250')}
        {legendDot(PAINT_RED, '판금 250+')}
        {legendDot('#C8D1DD', '미측정')}
      </div>
      <div style={{ borderTop: '1px solid #E8EDF3', marginTop: 10, paddingTop: 10 }}>
        {PAINT_GROUPS.map(function(g) {
          return (
            <div key={g[0]} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#98a2b3', marginBottom: 5 }}>{g[0]}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{g[1].map(paintChip)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
